import { useContext, useState } from "react";
import { Context } from "../../../context/Context";
import { Button, Check, Checkbox, Pencil, Select, Tags, X } from "../../ui";
import { bothNames } from "../topActions/shared";
import AiImportField, { ProvenanceBadge } from "./AiImportField";
import { aiImportText } from "./aiImportText";
import { ALLERGENS, allergenLabel, formatMoney, hasValue, isAiField, needsCheck, pairText, percent } from "./aiImportModel";

const ISSUE_VARIANT = (code) => (/UNRESOLVED|REQUIRED|INVALID/.test(code) ? "danger" : "warning");

// Allergen hints: shown as chips, confirmed or corrected by the reviewer.
function AllergenField({ field, disabled, onOp }) {
  const { lang } = useContext(Context);
  const t = aiImportText(lang);
  const [editing, setEditing] = useState(null);
  const value = field.value || { likely: [], possible: [] };
  const toggle = (group, code) =>
    setEditing((prev) => {
      const next = { likely: [...prev.likely], possible: [...prev.possible] };
      const other = group === "likely" ? "possible" : "likely";
      next[other] = next[other].filter((c) => c !== code);
      next[group] = next[group].includes(code) ? next[group].filter((c) => c !== code) : [...next[group], code];
      return next;
    });
  return (
    <div className={`ai-import-field ai-import-allergens ${needsCheck(field) ? "needs-check" : ""} ${isAiField(field) ? "is-ai" : ""}`}>
      <div className="ai-import-field-head">
        <span className="ai-import-field-label">{t.fields.allergens}</span>
        <ProvenanceBadge field={field} />
      </div>
      {editing ? (
        <div className="ai-import-allergen-edit">
          {["likely", "possible"].map((group) => (
            <fieldset key={group}>
              <legend className="menu-secondary">{t[group]}</legend>
              {Object.keys(ALLERGENS).map((code) => (
                <Checkbox key={code} label={allergenLabel(code, lang)} checked={editing[group].includes(code)} onChange={() => toggle(group, code)} />
              ))}
            </fieldset>
          ))}
          <span className="ai-import-field-actions">
            <Button type="button" variant="success" appearance="soft" size="xs" rounded icon={<Check size={13} />} onClick={() => { onOp({ op: "setField", value: editing }); setEditing(null); }}>
              {t.save}
            </Button>
            <Button type="button" variant="warning" appearance="soft" size="xs" rounded onClick={() => setEditing(null)}>
              {t.cancel}
            </Button>
          </span>
        </div>
      ) : (
        <div className="ai-import-field-view">
          <span className="ai-import-allergen-chips">
            {!hasValue(field) && <span className="ai-import-field-value">{field.provenance === "USER_VERIFIED" ? t.noAllergens : t.empty}</span>}
            {value.likely.map((code) => (
              <Tags key={code} variant="warning" size="sm">
                {allergenLabel(code, lang)}
              </Tags>
            ))}
            {value.possible.length > 0 && <span className="menu-secondary">{t.possible}:</span>}
            {value.possible.map((code) => (
              <Tags key={code} variant="neutral" size="sm">
                {allergenLabel(code, lang)}
              </Tags>
            ))}
          </span>
          {!disabled && (
            <span className="ai-import-field-actions">
              {field.provenance !== "USER_VERIFIED" && (
                <Button type="button" variant="success" appearance="soft" size="xs" rounded icon={<Check size={13} />} aria-label={`${t.verify}: ${t.fields.allergens}`} title={t.verify} onClick={() => onOp({ op: "verifyField" })} />
              )}
              <Button type="button" variant="primary" appearance="soft" size="xs" rounded icon={<Pencil size={13} />} aria-label={`${t.edit}: ${t.fields.allergens}`} title={t.edit} onClick={() => setEditing({ likely: [...value.likely], possible: [...value.possible] })} />
              {isAiField(field) && hasValue(field) && (
                <Button type="button" variant="danger" appearance="soft" size="xs" rounded icon={<X size={13} />} aria-label={`${t.reject}: ${t.fields.allergens}`} title={t.reject} onClick={() => onOp({ op: "rejectField" })} />
              )}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// Existing live entries this draft entry may duplicate, and the decision.
export function DuplicateResolver({ entity, entry, decisions, disabled, onOp }) {
  const { lang } = useContext(Context);
  const t = aiImportText(lang);
  const duplicate = entry.duplicate;
  if (!duplicate?.candidates?.length) return null;
  const targetKey = entity === "item" ? "targetItemId" : "targetAdditionId";
  const idKey = entity === "item" ? "itemId" : "additionId";
  const target = duplicate[targetKey] || duplicate.candidates[0][idKey];
  const pending = !duplicate.decision;
  return (
    <div className={`ai-import-duplicate ${pending ? "is-pending" : ""}`} role="group" aria-label={t.duplicate.title}>
      <div className="ai-import-field-head">
        <strong>{t.duplicate.title}</strong>
        <Tags variant={duplicate.strength === "strong" ? "info" : "warning"} size="sm">
          {t.duplicate[duplicate.strength]}
        </Tags>
      </div>
      <ul className="ai-import-candidates">
        {duplicate.candidates.map((c) => (
          <li key={c[idKey]}>
            <span dir="auto">{bothNames(c.name || c.title)}</span>
            {c.price !== undefined && c.price !== null && <span className="menu-secondary">{formatMoney(c.price, t)}</span>}
            <span className="menu-secondary">{t.duplicate.score(percent(c.score))}</span>
          </li>
        ))}
      </ul>
      <div className="ai-import-duplicate-controls">
        <Select
          label={t.duplicate.pick}
          placeholder={t.duplicate.pick}
          value={duplicate.decision || ""}
          disabled={disabled}
          options={decisions.map((d) => ({ value: d, label: t.duplicate.decisions[d] }))}
          onChange={(e) => onOp({ op: "setDuplicateDecision", entity, key: entry.key, decision: e.target.value, targetId: target })}
        />
        {duplicate.candidates.length > 1 && ["reuse", "update"].includes(duplicate.decision) && (
          <Select
            label={t.duplicate.target}
            value={String(target)}
            disabled={disabled}
            options={duplicate.candidates.map((c) => ({ value: String(c[idKey]), label: bothNames(c.name || c.title) }))}
            onChange={(e) => onOp({ op: "setDuplicateDecision", entity, key: entry.key, decision: duplicate.decision, targetId: e.target.value })}
          />
        )}
      </div>
    </div>
  );
}

export default function AiImportItemCard({ item, sections, canEdit, onOps }) {
  const { lang } = useContext(Context);
  const t = aiImportText(lang);
  const excluded = item.status === "exclude";
  const disabled = !canEdit;
  const fieldOp = (field) => (op) => onOps([{ ...op, entity: "item", key: item.key, field }]);
  const discount = item.discount?.value;
  const priceDisplay = (value) => {
    if (value === null || value === undefined) return t.empty;
    if (discount?.type === "amount") return `${formatMoney(value - discount.value, t)} (${t.discountFrom(formatMoney(value, t))})`;
    if (discount?.type === "percentage") return `${formatMoney(value * (1 - discount.value / 100), t)} (${t.discountFrom(formatMoney(value, t))})`;
    return formatMoney(value, t);
  };
  const issues = (item.issues || []).filter((code) => t.issues[code]);

  return (
    <article className={`ai-import-item ${excluded ? "is-excluded" : ""} ${issues.length ? "has-issues" : ""}`} aria-label={pairText(item.name, lang)}>
      <header className="ai-import-item-head">
        <h4 dir="auto">{pairText(item.name, lang) || t.empty}</h4>
        <span className="ai-import-item-tags">
          {excluded && <Tags variant="neutral" size="sm">{t.excluded}</Tags>}
          {!excluded && issues.map((code) => (
            <Tags key={code} variant={ISSUE_VARIANT(code)} size="sm">
              {t.issues[code]}
            </Tags>
          ))}
        </span>
        {canEdit && (
          <Button type="button" variant={excluded ? "success" : "warning"} appearance="soft" size="xs" rounded onClick={() => onOps([{ op: "setItemStatus", key: item.key, status: excluded ? "include" : "exclude" }])}>
            {excluded ? t.include : t.exclude}
          </Button>
        )}
      </header>
      {!excluded && (
        <>
          <div className="ai-import-fields">
            <AiImportField label={t.fields.nameAr} field={item.name.ar} dir="rtl" disabled={disabled} onOp={fieldOp("name.ar")} />
            <AiImportField label={t.fields.nameEn} field={item.name.en} dir="ltr" disabled={disabled} onOp={fieldOp("name.en")} />
            <AiImportField label={t.fields.price} field={item.price} type="number" display={priceDisplay} disabled={disabled} onOp={fieldOp("price")} />
            <AiImportField label={t.fields.calories} field={item.calories} type="number" display={(v) => (v ? `${v} ${t.kcal}` : t.empty)} disabled={disabled} onOp={fieldOp("calories")} allowVerifyEmpty />
            <AllergenField field={item.allergens} disabled={disabled} onOp={fieldOp("allergens")} />
            <AiImportField label={t.fields.descriptionAr} field={item.description.ar} type="longText" dir="rtl" disabled={disabled} onOp={fieldOp("description.ar")} />
            <AiImportField label={t.fields.descriptionEn} field={item.description.en} type="longText" dir="ltr" disabled={disabled} onOp={fieldOp("description.en")} />
          </div>
          <div className="ai-import-item-foot">
            <Select
              label={t.fields.section}
              value={item.sectionKey}
              disabled={disabled}
              fullWidth={false}
              options={sections.map((s) => ({ value: s.key, label: pairText(s.name, lang) || s.key }))}
              onChange={(e) => onOps([{ op: "moveItem", key: item.key, sectionKey: e.target.value }])}
            />
            {(item.sourceText || item.ambiguity) && (
              <p className="menu-secondary ai-import-source-text" dir="auto">
                {item.sourceText && <>{t.sourceText}: “{item.sourceText}”</>}
                {item.ambiguity && <> · {t.ambiguity}: {item.ambiguity}</>}
              </p>
            )}
          </div>
          <DuplicateResolver entity="item" entry={item} decisions={["create", "reuse", "update", "ignore"]} disabled={disabled} onOp={(op) => onOps([op])} />
        </>
      )}
    </article>
  );
}
